'use client';

import {Alert} from 'common/components/alert';
import {trackAuthErrorShown} from 'iq-auth/tracking';
import {useEffect, useState} from 'react';
import {useRouter, useSearchParams} from 'next/navigation';

/**
 * Recovery messages shown after an OAuth flow is bounced to the other page
 * (see oauth_callback.tsx). Keyed by the `?error=` code carried in the redirect.
 */
const MESSAGES: Record<string, string> = {
  'signup-user-exists': 'You already have an account. Log in below to continue.',
  'login-user-not-found': "We couldn't find an account for that email. Sign up to get started.",
};

/**
 * Renders a recovery banner for a known `?error=` code, or nothing otherwise.
 *
 * Reads the code from the querystring itself, so it MUST be wrapped in a
 * <Suspense> boundary — that scopes the `useSearchParams` CSR bailout to just
 * this banner and keeps the rest of the page statically rendered. The code is
 * captured once on mount and the param is stripped from the URL, so the banner
 * survives the cleanup and the error doesn't outlive a refresh. `dismissed` lets
 * the parent hide it (e.g. once the user starts the upload).
 */
export function AuthError({
  variant = 'light',
  dismissed = false,
}: {
  variant?: 'light' | 'dark';
  dismissed?: boolean;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Capture once, and only if it's a code we actually recognise. Stripping the
  // param below updates searchParams, which would otherwise clear the banner
  // mid-flow. An unknown code stays null (never rendered) but is still stripped.
  const [code] = useState(() => {
    const raw = searchParams.get('error');
    return raw && raw in MESSAGES ? raw : null;
  });

  useEffect(() => {
    // Track the recovery banner the user actually landed on (covers both the
    // signup→login and login→signup OAuth bounces).
    if (code) void trackAuthErrorShown(code as 'signup-user-exists' | 'login-user-not-found');
    // Strip any `error` param — known or not — so it doesn't survive a refresh.
    if (!searchParams.has('error')) return;
    const params = new URLSearchParams(searchParams.toString());
    params.delete('error');
    const query = params.toString();
    router.replace(query ? `?${query}` : window.location.pathname, {scroll: false});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const message = code ? MESSAGES[code] : undefined;
  if (!message || dismissed) return null;

  return (
    <Alert severity='warning' variant={variant}>
      {message}
    </Alert>
  );
}
